import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "../../i18n/useTranslation";
import { apiService } from "../../api/apiService";

const HomePricing = ({ inPagePricing }) => {
  const { t, language } = useTranslation();
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payPlan, setPayPlan] = useState("");
  const [payPlanPrice, setPayPlanPrice] = useState(0);
  const [isPaying, setIsPaying] = useState(false);
  const [orderData, setOrderData] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);
  const pollingIntervalRef = useRef(null);

  // 滚动到页面顶部
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // 查询订单状态
  const checkOrderStatus = async (orderNo) => {
    try {
      const response = await apiService.getOrderStatus(orderNo);
      if (response.success && response.data) {
        const statusData = response.data;
        setOrderStatus(statusData);
        
        // 如果订单已支付，停止轮询并处理成功逻辑
        if (statusData.status_text === "已支付" || statusData.status === 1) {
          setIsPolling(false);
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          
          // 设置支付成功状态，保持弹窗显示成功信息
          setIsPaymentSuccess(true);
          
          // 滚动到页面顶部
          setTimeout(() => {
            scrollToTop();
          }, 100);
        }
        // 如果订单支付失败、已取消或其他失败状态，停止轮询并显示失败提示
        else if (
          statusData.status_text === "支付失败" ||
          statusData.status_text === "已取消" ||
          statusData.status_text === "已退款" ||
          (statusData.status !== undefined && statusData.status !== 0 && statusData.status !== 1)
        ) {
          setIsPolling(false);
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          
          // 显示失败消息
          alert(t("payFailedMessage", { 
            status: statusData.status_text || t("payOrderFailed")
          }));
          
          // 不关闭弹窗，让用户看到失败状态，可以选择重新支付或关闭
        }
      }
    } catch (error) {
      console.error("Check order status error:", error);
      // 轮询失败不中断，继续轮询（可能是网络问题）
    }
  };

  // 开始轮询订单状态
  const startPolling = (orderNo) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    setIsPolling(true);
    
    // 立即查询一次
    checkOrderStatus(orderNo);
    
    // 每3秒轮询一次
    pollingIntervalRef.current = setInterval(() => {
      checkOrderStatus(orderNo);
    }, 3000);
  };

  // 停止轮询
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPolling(false);
  };

  // 组件卸载时清理轮询
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const openPayModal = async (plan, price) => {
    setPayPlan(plan);
    setPayPlanPrice(price);
    setOrderData(null);
    setOrderStatus(null);
    stopPolling();
    
    // 先创建订单，成功后再打开弹窗，避免空弹窗过渡
    setIsPaying(true);
    try {
      const paymentData = {
        product_name: plan,
        amount: price,
        currency: "CNY",
      };

      const response = await apiService.createPayment(paymentData);
      
      if (response.success && response.data) {
        setOrderData(response.data);
        // 订单创建成功后再打开弹窗
        setPayModalOpen(true);
        // 开始轮询订单状态
        if (response.data.order_no) {
          startPolling(response.data.order_no);
        }
      } else {
        alert(response.message || t("payErrorFailed"));
      }
    } catch (error) {
      console.error("Payment error:", error);
      const errorMessage = error.response?.data?.message || error.message || t("payErrorFailed");
      alert(errorMessage);
    } finally {
      setIsPaying(false);
    }
  };

  const closePayModal = () => {
    setPayModalOpen(false);
    setIsPaying(false);
    setOrderData(null);
    setOrderStatus(null);
    setIsPaymentSuccess(false);
    stopPolling();
  };

  return (
    <section
      className="py-20 xl:py-24 bg-white bg-center"
      style={{ backgroundImage: "url('/img/pattern-white.svg')" }}
    >
      <div className="container">
        <div className="text-center">
          <span className="inline-block py-px px-2 mb-4 text-xs leading-5 text-blue-500 bg-blue-100 font-medium uppercase rounded-full">
            {t("pricingTag")}
          </span>
          {inPagePricing ? (
            <h1 className="mb-4 text-2xl md:text-3xl !leading-tight text-slate-800 font-bold tracking-tight">
              {t("pricingTitle")}
            </h1>
          ) : (
            <h2 className="mb-4 text-2xl md:text-3xl !leading-tight text-slate-800 font-bold tracking-tight">
              {t("pricingTitle")}
            </h2>
          )}

          <p className="mb-12 text-base md:text-lg text-slate-500">
            {t("pricingDesc")}
          </p>
        </div>
        <div className="flex flex-wrap justify-center -mx-4">
          <div className="w-full md:w-1/2 lg:w-1/3 p-4">
            <div className="flex flex-col pt-8 pb-8 bg-slate-50 rounded-md shadow-lg shadow-gray-200 hover:scale-105 transition duration-500">
              <div className="px-8 pb-8">
                <h3 className="mb-6 text-base md:text-lg text-slate-800 font-medium">
                  {t("pricingTrialTitle")}
                </h3>
                <div className="mb-6">
                  <span className="relative -top-10 right-1 text-3xl text-slate-900 font-bold">
                    {t("pricingCurrencySymbol")}
                  </span>
                  <span className="text-6xl md:text-7xl !leading-normal text-slate-900 font-medium">
                    {t("pricingTrialPrice")}
                  </span>
                  <span className="inline-block ml-1 text-slate-500 ">
                    {t("pricingTrialValidity")}
                  </span>
                </div>
                <button
                  className="inline-block py-4 px-7 mb-4 w-full text-base md:text-lg !leading-6 text-white font-medium text-center bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 rounded-md shadow-sm"
                  type="button"
                  onClick={() => openPayModal(t("pricingTrialTitle"), language === "zh" ? 10 : 5)}
                >
                  {t("pricingGetStarted")}
                </button>
              </div>
              <div className="border-b border-slate-100"></div>
              <ul className="self-start px-8 pt-8">
                <li className="flex items-center mb-3 text-slate-500 font-medium">
                  <svg
                    className="mr-3 w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    ></path>
                  </svg>
                  <span>{t("pricingTrialFeature1")}</span>
                </li>
                <li className="flex items-center mb-3 text-slate-500 font-medium">
                  <svg
                    className="mr-3 w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    ></path>
                  </svg>
                  <span>{t("pricingTrialFeature2")}</span>
                </li>
                <li className="flex items-center mb-3 text-slate-500 font-medium">
                  <svg
                    className="mr-3 w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    ></path>
                  </svg>
                  <span>{t("pricingTrialFeature3")}</span>
                </li>
                <li className="flex items-center text-slate-500 font-medium">
                  <svg
                    className="mr-3 w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    ></path>
                  </svg>
                  <span>{t("pricingTrialFeature4")}</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="w-full md:w-1/2 lg:w-1/3 p-4">
            <div className="flex flex-col pt-8 pb-8 bg-slate-50 rounded-md shadow-lg shadow-gray-200 hover:scale-105 transition duration-500">
              <div className="px-8 pb-8">
                <div className="flex flex-wrap items-center justify-between mb-6">
                  <h3 className="mr-3 text-base md:text-lg text-slate-800 font-medium">
                    {t("pricingStandardTitle")}
                  </h3>
                  <span className="inline-block py-px px-2 text-xs leading-5 text-white bg-blue-600 font-medium uppercase rounded-full">
                    {t("pricingHotTag")}
                  </span>
                </div>
                <div className="mb-6">
                  <span className="relative -top-10 right-1 text-3xl text-slate-900 font-bold">
                    {t("pricingCurrencySymbol")}
                  </span>
                  <span className="text-6xl md:text-7xl !leading-normal text-slate-900 font-medium">
                    {t("pricingStandardPrice")}
                  </span>
                  <span className="inline-block ml-1 text-slate-500 ">
                    / {t("pricingPerDeviceMonth")}
                  </span>
                </div>
                <button
                  className="inline-block py-4 px-7 mb-4 w-full text-base md:text-lg !leading-6 text-white font-medium text-center bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 rounded-md shadow-sm"
                  type="button"
                  onClick={() => openPayModal(t("pricingStandardTitle"), language === "zh" ? 100 : 50)}
                >
                  {t("pricingGetStarted")}
                </button>
              </div>
              <div className="border-b border-slate-100"></div>
              <ul className="self-start px-8 pt-8">
                <li className="flex items-center mb-3 text-slate-500 font-medium">
                  <svg
                    className="mr-3 w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    ></path>
                  </svg>
                  <span>{t("pricingStandardFeature1")}</span>
                </li>
                <li className="flex items-center mb-3 text-slate-500 font-medium">
                  <svg
                    className="mr-3 w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    ></path>
                  </svg>
                  <span>{t("pricingStandardFeature2")}</span>
                </li>
                <li className="flex items-center mb-3 text-slate-500 font-medium">
                  <svg
                    className="mr-3 w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    ></path>
                  </svg>
                  <span>{t("pricingStandardFeature3")}</span>
                </li>
                <li className="flex items-center text-slate-500 font-medium">
                  <svg
                    className="mr-3 w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    ></path>
                  </svg>
                  <span>{t("pricingStandardFeature4")}</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="w-full md:w-1/2 lg:w-1/3 p-4">
            <div className="flex flex-col pt-8 pb-8 bg-slate-50 rounded-md shadow-lg shadow-gray-200 hover:scale-105 transition duration-500">
              <div className="px-8 pb-8">
                <h3 className="mb-6 text-base md:text-lg text-slate-800 font-medium">
                    {t("pricingEnterpriseTitle")}
                </h3>
                <div className="mb-6">
                  <span className="relative -top-10 right-1 text-3xl text-slate-900 font-bold">
                    {t("pricingCurrencySymbol")}
                  </span>
                  <span className="text-6xl md:text-7xl !leading-normal	 text-slate-900 font-medium">
                    {t("pricingEnterprisePrice")}
                  </span>
                  <span className="inline-block ml-1 text-slate-500 ">
                    / {t("pricingPerDeviceMonth")}
                  </span>
                </div>
                <button
                  className="inline-block py-4 px-7 mb-4 w-full text-base md:text-lg !leading-6 text-white font-medium text-center bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 rounded-md shadow-sm"
                  type="button"
                  onClick={() => openPayModal(t("pricingEnterpriseTitle"), language === "zh" ? 1000 : 500)}
                >
                  {t("pricingGetStarted")}
                </button>
              </div>
              <div className="border-b border-slate-100"></div>
              <ul className="self-start px-8 pt-8">
                <li className="flex items-center mb-3 text-slate-500 font-medium">
                  <svg
                    className="mr-3 w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    ></path>
                  </svg>
                  <span>{t("pricingEnterpriseFeature1")}</span>
                </li>
                <li className="flex items-center mb-3 text-slate-500 font-medium">
                  <svg
                    className="mr-3 w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    ></path>
                  </svg>
                  <span>{t("pricingEnterpriseFeature2")}</span>
                </li>
                <li className="flex items-center mb-3 text-slate-500 font-medium">
                  <svg
                    className="mr-3 w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    ></path>
                  </svg>
                  <span>{t("pricingEnterpriseFeature3")}</span>
                </li>
                <li className="flex items-center text-slate-500 font-medium">
                  <svg
                    className="mr-3 w-6 h-6 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    ></path>
                  </svg>
                  <span>{t("pricingEnterpriseFeature4")}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      {payModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 px-4">
          <div className="w-full max-w-md bg-white rounded-xl shadow-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="text-lg font-semibold text-slate-800">{t("payModalTitle")}</div>
              <button
                type="button"
                className="text-slate-400 hover:text-slate-600"
                onClick={closePayModal}
              >
                ×
              </button>
            </div>
            {orderData ? (
              <>
                {isPaymentSuccess ? (
                  <div className="text-center mb-4">
                    <div className="flex justify-center mb-4">
                      <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
                        <svg
                          className="w-10 h-10 text-green-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          ></path>
                        </svg>
                      </div>
                    </div>
                    <div className="text-lg font-semibold text-green-600 mb-2">
                      {t("paySuccessMessage", { plan: payPlan })}
                    </div>
                    <div className="text-sm text-slate-600 mb-4">
                      {t("paySuccessDesc")}
                    </div>
                  </div>
                ) : (
                  <div className="text-center mb-4">
                    {orderData.qrCodeUrl && (
                      <>
                        <div className="text-sm text-slate-600 mb-2">{t("payOrderQRCodeHint")}</div>
                        <div className="flex justify-center mb-4">
                          <img
                            src={orderData.qrCodeUrl}
                            alt={t("payQRCode")}
                            className="w-48 h-48 border border-slate-200 rounded-lg"
                          />
                        </div>
                      </>
                    )}
                    {!orderData.qrCodeUrl && (
                      <div className="text-sm text-slate-600 mb-2">{t("payOrderCreatedMessage")}</div>
                    )}
                    {isPolling && (
                      <div className="flex items-center justify-center gap-2 text-sm text-blue-600 mb-4">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span>{t("payPollingStatus")}</span>
                      </div>
                    )}
                    {orderStatus && (
                      <div className="text-sm mb-4">
                        <span className="text-slate-600">{t("payOrderStatus")}: </span>
                        <span className={`font-semibold ${
                          orderStatus.status_text === "已支付" || orderStatus.status === 1
                            ? "text-green-600"
                            : orderStatus.status_text === "支付失败" ||
                              orderStatus.status_text === "已取消" ||
                              orderStatus.status_text === "已退款" ||
                              (orderStatus.status !== undefined && orderStatus.status !== 0 && orderStatus.status !== 1)
                            ? "text-red-600"
                            : "text-orange-600"
                        }`}>
                          {orderStatus.status_text || t("payOrderPending")}
                        </span>
                      </div>
                    )}
                    {!orderData.qrCodeUrl && (
                      <div className="text-xs text-slate-500 mb-4">{t("payQRCodeHint")}</div>
                    )}
                  </div>
                )}
                <div className="border-t border-slate-200 pt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t("payOrderNo")}:</span>
                    <span className="font-medium text-slate-800">{orderData.order_no}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t("payOrderDate")}:</span>
                    <span className="font-medium text-slate-800">{orderData.order_date} {orderData.order_time}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t("payProductName")}:</span>
                    <span className="font-medium text-slate-800">{orderData.product_name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">{t("payAmount")}:</span>
                    <span className="font-semibold text-blue-600">¥{orderData.amount || orderData.payAmount}</span>
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-4">
                  <button
                    type="button"
                    className={`px-4 py-2 rounded-lg ${
                      isPaymentSuccess
                        ? "bg-blue-600 text-white hover:bg-blue-700"
                        : "border border-slate-200 text-slate-600 hover:bg-slate-100"
                    }`}
                    onClick={closePayModal}
                  >
                    {isPaymentSuccess ? t("payModalClose") : t("payCancelPayment")}
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}
    </section>
  );
};

export default HomePricing;
